import Command from '@ckeditor/ckeditor5-core/src/command';

import { CkeditorConstants } from '../../src/utils/ckeditorconstants';
import { findLinkOrCitationElement } from '../../src/utils';

const {
  htmlElement: { PARAGRAPH },
  PLUGINS: { TRACKCHANGE_EDITING },
  COMMANDS: { CMD_TRACK_CHANGES },
  helperAttributes: { SUBTEMPLATE_NAME, },
  htmlAttributes: { ID },
  rightClickOptions: { REMOVE_SUBTEMPLATE, },
  trackchangesMarkup: { SUGGESTION_END_AFTER, SUGGESTION },
  elementClass: { suggestion_insertion_marker },
  suggestionType: { INSERTION, DELETION }
} = CkeditorConstants;

export default class DeleteInBatchByIdCommand extends Command {
  execute(ids, details) {
    this.batchDeleteById(ids, details);
  }

  batchDeleteById(ids, details) {
    const editor = this.editor;
    const trackChangesEditing = editor.plugins.get(TRACKCHANGE_EDITING);
    const domElement = editor.editing.view.domRoots.get('main');
    const isEnabledTrackChange = editor.commands.get(CMD_TRACK_CHANGES).value;
    const markerInRange = details.eventName === REMOVE_SUBTEMPLATE ? this._getMarkersInRange(details.deletableSubTempRange) : [];

    editor.model.change((writer) => {
      if (editor.selectedSubtemplateId && !isEnabledTrackChange) {
        ids = [editor.selectedSubtemplateId, ...ids];
      }
      ids?.forEach((id, _idx) => {
        const elementToRemove = domElement.querySelector(`[${ID}='${id}']`);
        const linkViewElement = editor.editing.view.domConverter.mapDomToView(elementToRemove);
        const elementSelected = editor.editing.mapper.toModelElement(linkViewElement);
        if (elementSelected) {
          const elementName = findLinkOrCitationElement(elementSelected)
          /**
           * When track changes is not enabled
           * then unwrap the link element and remove subtemplate
           */
          if (!isEnabledTrackChange) {
            elementName === 'link' ? this._removeLinkAndSuggestion(elementSelected, trackChangesEditing, writer) : writer.remove(elementSelected);
          }
          /**
           *  When track changes is enabled
           */
          else {
            if (elementToRemove?.firstChild?.classList?.contains(suggestion_insertion_marker)) {
              const suggestionId = elementToRemove.firstChild.getAttribute(SUGGESTION);
              const suggestion = trackChangesEditing.getSuggestion(suggestionId);
              suggestion?.type == INSERTION && trackChangesEditing.acceptSuggestion(suggestion);
            }
            const range = editor.model.createRangeIn(elementSelected);
            trackChangesEditing.markDeletion(range, 'deleteLink', {
              commandParams: {
                linkAddress: elementSelected.getAttribute('about') || '',
                removeLinkFromTrackChanges: true,
              },
              commandName: CkeditorConstants.command.DELETE_IN_BATCH_BY_ID,
            });
          }
        }
      }
      );
      if (details.eventName === REMOVE_SUBTEMPLATE && isEnabledTrackChange) {
        const itemsInSubtemplateRange = [...details.deletableSubTempRange.getWalker({ ignoreElementEnd: true, shallow: false })];
        const subTemplateType = editor.editing.mapper.toViewElement(itemsInSubtemplateRange[0].item).hasAttribute(SUBTEMPLATE_NAME) ? 'editableSubtemplate' : 'nonEditableSubtemplate';
        const suggestionsToDelete = _.filter(markerInRange, (marker) => {
          if (marker.name?.split?.(':')?.[0].includes('suggestion')) {
            const suggestionId = marker.name.includes(INSERTION) || marker.name.includes(DELETION) ? marker.name.split(':')[2] : marker.name.split(':')[3];
            if (suggestionId) {
              return trackChangesEditing.getSuggestion(suggestionId);
            }
          }
        });
        this._removeInsertionSuggestions(suggestionsToDelete, writer, trackChangesEditing);
        this._markDeleteForRemoveSubtemplate({ range: details.deletableSubTempRange, trackChangesEditing });
      }
    });
  }

  _removeInsertionSuggestions(suggestionsToDelete, writer, trackChangesEditing) {

    const getItemsInSuggestion = (suggestion) => {
      const itemsArr = [];
      _.forEach(suggestion.getRanges(), value => {
        itemsArr.push(..._.map(value.getWalker({ ignoreElementEnd: true, shallow: false }), (node) => node.item));
      });
      return itemsArr;
    }

    _.forEach(suggestionsToDelete, (removableSuggestion) => {
      const itemToDelete = removableSuggestion.type === INSERTION && getItemsInSuggestion(removableSuggestion);
      _.forEach(itemToDelete, (item) => {
        if (item.name === PARAGRAPH && item._children?.length > 0 && item.parent?.hasAttribute(dataSubtemplateAllow)) {
          return;
        }
        writer.remove(item);
      });
      removableSuggestion.type === INSERTION && trackChangesEditing.discardSuggestion(removableSuggestion);
    });
  }

  _markDeleteForRemoveSubtemplate(props) {
    const { trackChangesEditing, range } = props;
    try {
      const suggestions = _.filter([...this.editor.model.markers.getMarkersIntersectingRange(range)], (marker) => marker.name.startsWith('suggestion:deletion') || marker.name.startsWith('suggestion:insertion'));
      _.forEach(suggestions, (suggestion) => {
        const suggestionId = suggestion.name?.split(':')[2];
        const s = trackChangesEditing.getSuggestion(suggestionId);
        s?.type == INSERTION && trackChangesEditing.acceptSuggestion(s);
      })
      
      for (const item of range.getWalker({ ignoreElementEnd: true, shallow: false })) {
        if (item.item.name === 'numberedcitation') {
          const citationRange = this.editor.model.createRangeOn(item.item);
          trackChangesEditing.markDeletion(citationRange, null, {
            commandParams: { removeSubtemplate: true }
          });
        }
      }
      
      trackChangesEditing.markDeletion(
        range,
        null,
        {
          commandParams: { removeSubtemplate: true }
        }
      );
    } catch (err) {
      console.warn(err);
      throw Error(err)
    }
  }

  _getMarkersInRange(range) {
    return [...this.editor.model.markers.getMarkersIntersectingRange(
      range
    )];
  }

  _removeLinkAndSuggestion(elementSelected, trackChangesEditing, writer) {
    const suggestionId = elementSelected._attrs.get(SUGGESTION_END_AFTER)?.split?.(':')?.[1];
    writer.unwrap(elementSelected);

    _.forEach(trackChangesEditing.getSuggestions(suggestionId), (suggestion) => {
      suggestion.type == INSERTION && trackChangesEditing.acceptSuggestion(suggestion);
    });
  }
}
